import type { GridBox } from "@reco/shared/src/types/gridbox";

import { mapToErpNumber } from "@/utils/product";
import RecommendationService from "@reco/service/src";
import { isShopTheLookResult } from "@reco/service/src/utils/helpers";
import { cleanBrandName, getCategoryLevel } from "@reco/service/src/utils/productHelpers";
import { FlexibleScenario, Scenario } from "@reco/shared/src/constants";

export function useRecommendations(product: Ref<GridBox | null>, erpNumber: string) {
  const { t } = useI18n();
  const { recoHost, recoDetailsHost } = useRecoConfig();

  const {
    data: productsData,
    pending: productsPending,
    error: productsError,
    execute: executeProducts,
  } = useAsyncData(
    `recommendations-products-${erpNumber}`,
    async () => {
      if (!product.value) {
        return null;
      }

      const recoService = new RecommendationService();
      const productsResponse = await recoService.getRecommendations({
        type: Scenario.PRODUCTS,
        recoHost,
        recoDetailsHost,
        min: "true",
      });

      if (isShopTheLookResult(productsResponse)) {
        return;
      }

      return {
        products: productsResponse?.recommendations || [],
        categoryPath: productsResponse?.categoryPath,
        categoryPathName: productsResponse?.categoryPathName,
      };
    },
    {
      immediate: !!product.value,
    },
  );

  watch(product, (val) => {
    if (val) {
      executeProducts();
    }
  });

  const {
    data: crossData,
    pending: crossPending,
    error: crossError,
    execute: executeCross,
  } = useAsyncData(
    `recommendations-cross-${erpNumber}`,
    async () => {
      if (!product.value || !productsData.value) {
        return null;
      }

      const excludeRecommendationItems: string[] = [erpNumber];
      excludeRecommendationItems.push(...mapToErpNumber(productsData.value.products));

      const recoService = new RecommendationService();
      const crossResponse = await recoService.getRecommendations({
        type: Scenario.CROSS,
        recoHost,
        recoDetailsHost,
        min: "true",
        exclude: excludeRecommendationItems,
      });

      if (isShopTheLookResult(crossResponse)) {
        return;
      }

      return { products: crossResponse?.recommendations || [] };
    },
    {
      immediate: !!(product.value && productsData.value),
    },
  );

  watch([product, productsData], ([newProduct, newProductsData]) => {
    if (newProduct && newProductsData) {
      executeCross();
    }
  });

  const {
    data: categoryData,
    pending: categoryPending,
    error: categoryError,
    execute: executeCategory,
  } = useAsyncData(
    `recommendations-bestsellers-${erpNumber}`,
    async () => {
      if (!product.value || !productsData.value || !crossData.value) {
        return null;
      }

      const categoryPath = productsData.value.categoryPath;
      if (!categoryPath) {
        return { products: [], heading: "" };
      }

      const productCategory = getCategoryLevel(2, categoryPath);
      if (!productCategory) {
        return { products: [], heading: "" };
      }

      const excludeRecommendationItems: string[] = [erpNumber];
      excludeRecommendationItems.push(...mapToErpNumber(productsData.value.products));
      excludeRecommendationItems.push(...mapToErpNumber(crossData.value.products));

      const recoService = new RecommendationService();
      const categoryResponse = await recoService.getRecommendations({
        type: FlexibleScenario.BESTSELLERS_CATEGORY_2_PRODUCTBASED_URL,
        contextId: productCategory,
        recoHost,
        recoDetailsHost,
        min: "true",
        exclude: excludeRecommendationItems,
      });

      if (isShopTheLookResult(categoryResponse)) {
        return;
      }

      let heading = "";
      if (productsData.value.categoryPathName) {
        const categoryName = getCategoryLevel(2, productsData.value.categoryPathName);
        heading = t("reco-frontend-next.RDP.bestsellers", { category: categoryName || "" });
      }

      return {
        products: categoryResponse?.recommendations || [],
        heading,
      };
    },
    {
      immediate: !!(product.value && productsData.value && crossData.value),
    },
  );

  watch([product, productsData, crossData], ([newProduct, newProductsData, newCrossData]) => {
    if (newProduct && newProductsData && newCrossData) {
      executeCategory();
    }
  });

  const {
    data: brandsData,
    pending: brandsPending,
    error: brandsError,
    execute: executeBrands,
  } = useAsyncData(
    `recommendations-brands-${erpNumber}`,
    async () => {
      if (!product.value || !productsData.value || !crossData.value || !categoryData.value) {
        return null;
      }

      if (!product.value?.brand.name) {
        return { products: [], heading: "" };
      }

      const brandName = cleanBrandName(product.value.brand.name);
      if (!brandName) {
        return { products: [], heading: "" };
      }

      const excludeRecommendationItems: string[] = [erpNumber];
      excludeRecommendationItems.push(...mapToErpNumber(productsData.value.products));
      excludeRecommendationItems.push(...mapToErpNumber(crossData.value.products));
      excludeRecommendationItems.push(...mapToErpNumber(categoryData.value.products));

      const recoService = new RecommendationService();
      const brandsResponse = await recoService.getRecommendations({
        type: FlexibleScenario.BESTSELLERS_BRAND_PRODUCTBASED_URL,
        contextId: brandName,
        recoHost,
        recoDetailsHost,
        min: "true",
        exclude: excludeRecommendationItems,
      });

      if (isShopTheLookResult(brandsResponse)) {
        return;
      }

      const heading = t("reco-frontend-next.RDP.brands", { brand: product.value?.brand.name || "" });

      return {
        products: brandsResponse?.recommendations || [],
        heading,
      };
    },
    {
      immediate: !!(product.value && productsData.value && crossData.value && categoryData.value),
    },
  );

  watch(
    [product, productsData, crossData, categoryData],
    ([newProduct, newProductsData, newCrossData, newCategoryData]) => {
      if (newProduct && newProductsData && newCrossData && newCategoryData) {
        executeBrands();
      }
    },
  );

  return {
    products: computed(() => productsData.value?.products || []),
    productsPending,
    productsError,

    cross: computed(() => crossData.value?.products || []),
    crossPending,
    crossError,

    category: computed(() => categoryData.value?.products || []),
    categoryPending,
    categoryError,

    brands: computed(() => brandsData.value?.products || []),
    brandsPending,
    brandsError,

    headings: computed(() => ({
      products: t("reco-frontend-next.RDP.products"),
      cross: t("reco-frontend-next.RDP.cross"),
      category: categoryData.value?.heading || "",
      brands: brandsData.value?.heading || "",
    })),
  };
}
